
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import styles from './AuthGuard.module.css';

const DEMO_MODE = false;

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const { user, loading, onboardingComplete, accountSecured } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    if (!DEMO_MODE && !loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  // First gate: secure the account (reset the admin-given password or link
  // SSO) before anything else. /secure-account is allowed through so the
  // account can complete the gate.
  useEffect(() => {
    if (!loading && user && !accountSecured && pathname !== '/secure-account') {
      navigate('/secure-account');
    }
  }, [user, loading, accountSecured, pathname, navigate]);

  // Second gate: signup onboarding. /signup lives outside this guard (it
  // enforces its own gates) and is now the whole flow — This deleted the
  // /setup wizard that used to follow it, so there is no second path to allow
  // through here; what signup cannot collect becomes the sidebar checklist.
  useEffect(() => {
    if (
      !loading &&
      user &&
      accountSecured &&
      !onboardingComplete &&
      pathname !== '/signup'
    ) {
      navigate('/signup');
    }
  }, [user, loading, accountSecured, onboardingComplete, pathname, navigate]);

  if (DEMO_MODE) return <>{children}</>;

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.inner}>
          <div className={styles.spinner} />
          <p className={styles.label}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return <>{children}</>;
}
